import React, { useState, useCallback, useEffect } from "react";
import Select, { components as ReactSelectComponents } from "react-select";
import styled from "styled-components";
import ModalAviso from "../modal-aviso";
import { TipoFormulario } from "../../services/constantes";

export interface SelectMultiTag {
    texto: string;
    cor: string;
}

export interface SelectMultiProps {
    dataSource: any[];
    initialSelected?: any[];
    onSelectionChange: (data: any[]) => void;
    optionLabelField: string;
    style?: React.CSSProperties;
    height?: string | number;
    modo: TipoFormulario;
    criticarRepeticao?: boolean;
    maxRecords?: number;
    orderByField?: string;
    multiValueBackground?: string;
    optionBackgroundField?: string;
    apenasLeitura?: boolean;
    // Tag colorida exibida à direita do label de cada opção (ex.: status do veículo).
    optionTag?: (item: any) => SelectMultiTag | null | undefined;
    /**
     * Texto do campo vazio. Útil quando "nada selecionado" tem significado próprio — na regra de
     * comissão por veículo, vazio quer dizer "todos os veículos", e o padrão "Selecione as opções"
     * esconderia isso.
     */
    placeholder?: string;
}

const StyledSelect = styled(Select)`
  font-size: 14px;
  .react-select__control {
    border: 1px solid #e5e7eb;
    box-shadow: none;
    &:hover {
      border-color: #d1d5db;
    }
    &:focus-within {
      border-color: #3b82f6;
    }
  }
  .react-select__menu {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 9999;
  }
  .react-select__menu-list {
    max-height: 300px;
    overflow-y: auto;
    padding: 4px;
  }
  .react-select__multi-value {
    border-radius: 4px;
  }
  .react-select__multi-value__label {
    color: #374151;
    font-size: 13px;
  }
`;

const TagChip: React.FC<{ tag: SelectMultiTag }> = ({ tag }) => (
    <span style={{
        backgroundColor: tag.cor, color: '#fff', borderRadius: 10,
        padding: '1px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
        {tag.texto}
    </span>
);

const Option = (props: any) => {
    const tag = props.selectProps.optionTag?.(props.data.value);
    return (
        <ReactSelectComponents.Option {...props}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span>{props.children}</span>
                {tag && <TagChip tag={tag} />}
            </div>
        </ReactSelectComponents.Option>
    );
};

const MultiValue = (props: any) => {
    const backgroundColor = props.data.value[props.selectProps.optionBackgroundField] || 
                            props.selectProps.multiValueBackground || '#204579';
    
    return (
        <ReactSelectComponents.MultiValue 
            {...props}
            innerProps={{
                ...props.innerProps,
                style: {
                    backgroundColor,
                    borderRadius: '4px',
                }
            }}
        >
            <div style={{ color: '#ffffff', padding: '2px 6px' }}>
                {props.children}
            </div>
        </ReactSelectComponents.MultiValue>
    );
};

export const SelectMulti: React.FC<SelectMultiProps> = ({
    dataSource,
    initialSelected = [],
    onSelectionChange,
    optionLabelField,
    modo,
    criticarRepeticao = false,
    maxRecords,
    orderByField,
    multiValueBackground,
    optionBackgroundField,
    apenasLeitura,
    optionTag,
    placeholder
}) => {
    const [selectedOptions, setSelectedOptions] = useState<any[]>(initialSelected);
    const [showAviso, setShowAviso] = useState(false);
    const [avisoMessage, setAvisoMessage] = useState("");

    const labelFields = optionLabelField.includes(";")
        ? optionLabelField.split(";")
        : [optionLabelField];

    const options = React.useMemo(
        () =>
            dataSource.map((item: any) => ({
                label: labelFields.map((field) => item[field] || "").join(" - "),
                value: item,
            })),
        [dataSource, labelFields]
    );

    const selectedValues = React.useMemo(
        () =>
            selectedOptions.map((item) => ({
                label: labelFields.map((field) => item[field] || "").join(" - "),
                value: item,
            })),
        [selectedOptions, labelFields]
    );

    const sortData = (data: any[], field?: string) => {
        if (!field) return data;
        return [...data].sort((a, b) => {
            const valueA = a[field]?.toString().toLowerCase() || "";
            const valueB = b[field]?.toString().toLowerCase() || "";
            return valueA.localeCompare(valueB);
        });
    };

    useEffect(() => {
        const sortedData = sortData(selectedOptions, orderByField);
        if (JSON.stringify(sortedData) !== JSON.stringify(selectedOptions)) {
            setSelectedOptions(sortedData);
        }
    }, [selectedOptions, orderByField]);

    const handleChange = useCallback(
        (selectedItems: any[]) => {
            if (modo === TipoFormulario.tpVisualizacao) return;

            if (maxRecords !== undefined && selectedItems.length > maxRecords) {
                setAvisoMessage(`Limite de escolhas '${maxRecords}' atingido`);
                setShowAviso(true);
                return;
            }

            const newSelected = selectedItems.map((item) => item.value);

            if (criticarRepeticao) {
                const uniqueIds = new Set(newSelected.map((item) => item.id));
                if (uniqueIds.size !== newSelected.length) {
                    setAvisoMessage("Existem itens duplicados na seleção");
                    setShowAviso(true);
                    return;
                }
            }

            const sortedSelected = sortData(newSelected, orderByField);
            setSelectedOptions(sortedSelected);
            onSelectionChange(sortedSelected);
        },
        [modo, maxRecords, criticarRepeticao, onSelectionChange, orderByField]
    );

    const isViewMode = modo === TipoFormulario.tpVisualizacao;

    return (
        <>
            <ModalAviso
                type="recusado"
                messageAviso={avisoMessage}
                onClose={() => setShowAviso(false)}
                show={showAviso}
            />
            <StyledSelect
                classNamePrefix="react-select"
                value={selectedValues}
                onChange={handleChange}
                options={options}
                placeholder={placeholder ?? "Selecione as opções"}
                isMulti
                isDisabled={(apenasLeitura && apenasLeitura == true) ?? isViewMode}
                noOptionsMessage={() => "Nenhuma opção encontrada"}
                getOptionLabel={(e: any) => e.label}
                getOptionValue={(e: any) => e.value.id}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                components={{ MultiValue, Option }}
                styles={{
                    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    menu: (base: any) => ({ ...base, position: 'absolute', zIndex: 9999 }),
                }}
                multiValueBackground={multiValueBackground}
                optionBackgroundField={optionBackgroundField}
                optionTag={optionTag}
            />
        </>
    );
};